// packages/shared-theme/index.ts
import "./theme.css";
import "./color-dot.css";

export const THEME_STORAGE_KEY = "app-theme";

export type ThemeType = "light" | "dark" | "system";
export type ThemeColorMode = Exclude<ThemeType, "system">;

export interface ThemeColors {
  theme?: ThemeColorMode;
  primaryColor?: string;
  /** @deprecated 请使用 primaryColorHover */
  primaryHover?: string;
  primaryColorHover?: string;
  textPrimary?: string;
  textSecondary?: string;
  textTertiary?: string;
  textDisabled?: string;
  textQuaternary?: string;
  textSubtle?: string;
  successColor?: string;
  warningColor?: string;
  errorColor?: string;
  infoColor?: string;
  /** @deprecated 请使用 errorColor */
  deleteColor?: string;
  /** @deprecated 请使用 errorColor */
  deleteColorHover?: string;
}

export const getStoredTheme = (): ThemeType => {
  return (localStorage.getItem(THEME_STORAGE_KEY) as ThemeType) || "light";
};

export const setStoredTheme = (theme: ThemeType): void => {
  localStorage.setItem(THEME_STORAGE_KEY, theme);
};

export const applyTheme = (theme: ThemeType, element?: HTMLElement): void => {
  const target = element || document.documentElement;
  target.setAttribute("data-theme", theme);
};

/** 解析当前 DOM / 系统偏好下的实际浅深色（含 data-theme 为 system 等未落地值） */
export const resolveThemeColorMode = (
  element?: HTMLElement
): ThemeColorMode => {
  const target = element || document.documentElement;
  const theme = target.getAttribute("data-theme");
  if (theme === "dark" || theme === "light") return theme;
  if (typeof window === "undefined") return "light";

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/**
 * 应用自定义主题与文本色
 * @param colors 自定义颜色配置
 * @param element 目标元素，默认为 document.documentElement
 */
export const applyThemeColors = (
  colors: ThemeColors,
  element?: HTMLElement
): void => {
  const target = element || document.documentElement;
  const mode = colors.theme ?? resolveThemeColorMode(element);

  if (colors.primaryColor) {
    target.style.setProperty(
      `--custom-primary-color-${mode}`,
      colors.primaryColor
    );
  }

  const hover = colors.primaryColorHover ?? colors.primaryHover;
  if (hover) {
    target.style.setProperty(`--custom-primary-color-${mode}-hover`, hover);
  }

  if (colors.textPrimary) {
    target.style.setProperty(
      `--custom-text-primary-${mode}`,
      colors.textPrimary
    );
  }
  if (colors.textSecondary) {
    target.style.setProperty(
      `--custom-text-secondary-${mode}`,
      colors.textSecondary
    );
  }
  if (colors.textTertiary) {
    target.style.setProperty(
      `--custom-text-tertiary-${mode}`,
      colors.textTertiary
    );
  }
  if (colors.textDisabled) {
    target.style.setProperty(
      `--custom-text-disabled-${mode}`,
      colors.textDisabled
    );
  }
  if (colors.textQuaternary) {
    target.style.setProperty(
      `--custom-text-quaternary-${mode}`,
      colors.textQuaternary
    );
  }
  if (colors.textSubtle) {
    target.style.setProperty(`--custom-text-subtle-${mode}`, colors.textSubtle);
  }

  if (colors.successColor) {
    target.style.setProperty("--success-color", colors.successColor);
  }
  if (colors.warningColor) {
    target.style.setProperty("--warning-color", colors.warningColor);
  }
  if (colors.errorColor ?? colors.deleteColor) {
    target.style.setProperty(
      "--error-color",
      colors.errorColor ?? colors.deleteColor!
    );
  }
  if (colors.infoColor) {
    target.style.setProperty("--info-color", colors.infoColor);
  }
};

/**
 * 重置主题与文本色为默认值
 * @param element 目标元素
 */
export const resetThemeColors = (element?: HTMLElement): void => {
  const target = element || document.documentElement;
  target.style.removeProperty("--custom-primary-color-light");
  target.style.removeProperty("--custom-primary-color-light-hover");
  target.style.removeProperty("--custom-primary-color-dark");
  target.style.removeProperty("--custom-primary-color-dark-hover");
  target.style.removeProperty("--custom-text-primary-light");
  target.style.removeProperty("--custom-text-secondary-light");
  target.style.removeProperty("--custom-text-tertiary-light");
  target.style.removeProperty("--custom-text-disabled-light");
  target.style.removeProperty("--custom-text-quaternary-light");
  target.style.removeProperty("--custom-text-subtle-light");
  target.style.removeProperty("--custom-text-primary-dark");
  target.style.removeProperty("--custom-text-secondary-dark");
  target.style.removeProperty("--custom-text-tertiary-dark");
  target.style.removeProperty("--custom-text-disabled-dark");
  target.style.removeProperty("--custom-text-quaternary-dark");
  target.style.removeProperty("--custom-text-subtle-dark");
  target.style.removeProperty("--custom-btn-primary-text-light");
  target.style.removeProperty("--custom-btn-primary-text-dark");
  target.style.removeProperty("--custom-btn-deputy-text-light");
  target.style.removeProperty("--custom-btn-deputy-text-dark");
  target.style.removeProperty("--primary-color");
  target.style.removeProperty("--primary-color-hover");
  target.style.removeProperty("--text-primary");
  target.style.removeProperty("--text-secondary");
  target.style.removeProperty("--text-tertiary");
  target.style.removeProperty("--text-disabled");
  target.style.removeProperty("--text-quaternary");
  target.style.removeProperty("--text-subtle");
  target.style.removeProperty("--success-color");
  target.style.removeProperty("--warning-color");
  target.style.removeProperty("--error-color");
  target.style.removeProperty("--info-color");
  target.style.removeProperty("--delete-color");
  target.style.removeProperty("--delete-color-hover");
};

// ============================================================
//  壁纸相关 API
// ============================================================

export interface WallpaperSettings {
  enabled: boolean;
  url: string;
  mediaType?: "image" | "video";
  blur: number;
  brightness: number;
}

interface WallpaperTransitionState {
  timers: number[];
  preloadImage: HTMLImageElement | null;
  requestId: number;
  /** 当前正在预加载或过渡中的目标图片 URL（原始地址，非 CSS url()） */
  pendingUrl: string | null;
}

const wallpaperTransitionStates = new WeakMap<
  HTMLElement,
  WallpaperTransitionState
>();
const WALLPAPER_FADE_IN_MS = 280;
const WALLPAPER_VIDEO_CLASS = "eweadn-wallpaper-video";

const toWallpaperCssUrl = (url: string): string => {
  const escapedUrl = url.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escapedUrl}")`;
};

/** 归一化壁纸地址：仅用 pathname 判断是否同一张图，忽略 CDN 域名与签名 */
const normalizeWallpaperUrl = (targetUrl: string): string => {
  if (!targetUrl) return "";

  const rawUrl = targetUrl.replace(/^url\(["']?/, "").replace(/["']?\)$/, "");

  try {
    return new URL(rawUrl, window.location.origin).pathname;
  } catch {
    return rawUrl.split("?")[0].split("#")[0];
  }
};

const isSameWallpaperUrl = (a: string, b: string): boolean =>
  normalizeWallpaperUrl(a) === normalizeWallpaperUrl(b);

const isWallpaperVideoUrl = (url: string): boolean =>
  /\.mp4(?:[?#]|$)/i.test(normalizeWallpaperUrl(url));

const resolveWallpaperMediaType = (
  settings: WallpaperSettings
): "image" | "video" =>
  settings.mediaType === "video" || isWallpaperVideoUrl(settings.url)
    ? "video"
    : "image";

const applyWallpaperStyle = (
  target: HTMLElement,
  settings: Pick<WallpaperSettings, "blur" | "brightness">
): void => {
  target.style.setProperty("--wallpaper-blur", `${settings.blur}px`);
  target.style.setProperty(
    "--wallpaper-brightness",
    String(settings.brightness)
  );
};

/** 仅更新模糊/亮度，不触碰壁纸图层，避免调节滑块时白屏闪烁 */
export const updateWallpaperStyle = (
  settings: Pick<WallpaperSettings, "blur" | "brightness">,
  element?: HTMLElement
): void => {
  const target = element || document.documentElement;
  if (!target.hasAttribute("data-wallpaper")) return;
  applyWallpaperStyle(target, settings);
};

const getWallpaperTransitionState = (
  target: HTMLElement
): WallpaperTransitionState => {
  let state = wallpaperTransitionStates.get(target);
  if (!state) {
    state = {
      timers: [],
      preloadImage: null,
      requestId: 0,
      pendingUrl: null,
    };
    wallpaperTransitionStates.set(target, state);
  }
  return state;
};

const clearWallpaperTimers = (state: WallpaperTransitionState): void => {
  state.timers.forEach((timer) => window.clearTimeout(timer));
  state.timers = [];
};

const clearWallpaperPreload = (state: WallpaperTransitionState): void => {
  if (!state.preloadImage) return;
  state.preloadImage.onload = null;
  state.preloadImage.onerror = null;
  state.preloadImage.removeAttribute("src");
  state.preloadImage = null;
};

const clearWallpaperTransition = (target: HTMLElement): void => {
  const state = getWallpaperTransitionState(target);
  clearWallpaperTimers(state);
  clearWallpaperPreload(state);
  state.pendingUrl = null;
  target.removeAttribute("data-wallpaper-transitioning");
  target.removeAttribute("data-wallpaper-fade-out");
  target.style.removeProperty("--wallpaper-next-image");
};

const getWallpaperVideoElement = (target: HTMLElement): HTMLVideoElement => {
  const doc = target.ownerDocument || document;
  const mount = target === doc.documentElement ? doc.body : target;
  let video = doc.querySelector<HTMLVideoElement>(`.${WALLPAPER_VIDEO_CLASS}`);

  if (!video) {
    video = doc.createElement("video");
    video.className = WALLPAPER_VIDEO_CLASS;
    video.muted = true;
    video.loop = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute("aria-hidden", "true");
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    mount.prepend(video);
  }

  return video;
};

const removeWallpaperVideoElement = (target: HTMLElement): void => {
  const doc = target.ownerDocument || document;
  const video = doc.querySelector<HTMLVideoElement>(
    `.${WALLPAPER_VIDEO_CLASS}`
  );
  if (!video) return;
  video.pause();
  video.removeAttribute("src");
  video.load();
  video.remove();
};

const applyWallpaperVideo = (
  target: HTMLElement,
  settings: WallpaperSettings
): void => {
  clearWallpaperTransition(target);
  target.setAttribute("data-wallpaper", "true");
  applyWallpaperStyle(target, settings);
  target.style.removeProperty("--bg-primary");
  target.style.removeProperty("--bg-secondary");
  target.style.removeProperty("--bg-tertiary");

  const video = getWallpaperVideoElement(target);
  const currentVideoSrc = video.currentSrc || video.src;
  const isExactSameVideoUrl = Boolean(
    currentVideoSrc && currentVideoSrc === settings.url
  );
  const wasVideoMedia = target.getAttribute("data-wallpaper-media") === "video";
  const isVideoReady =
    wasVideoMedia && target.getAttribute("data-wallpaper-ready") === "true";

  /** 视频可播后再切图层，避免图→视频期间清掉图片露出白底 */
  const revealVideoLayer = (requestId: number) => {
    if (getWallpaperTransitionState(target).requestId !== requestId) return;
    target.setAttribute("data-wallpaper-media", "video");
    target.style.removeProperty("--wallpaper-image");
    target.style.removeProperty("--wallpaper-next-image");
    target.setAttribute("data-wallpaper-ready", "true");
    video.style.opacity = "1";
  };

  if (isExactSameVideoUrl && isVideoReady) {
    video.style.opacity = "1";
    void video.play().catch(() => undefined);
    return;
  }

  const requestId = getWallpaperTransitionState(target).requestId + 1;
  getWallpaperTransitionState(target).requestId = requestId;

  // 从图片切过来时先藏视频、保留当前图片；视频切视频时保留上一帧
  if (!wasVideoMedia) {
    video.style.opacity = "0";
  }

  video.oncanplay = () => {
    revealVideoLayer(requestId);
  };
  video.onerror = () => {
    if (getWallpaperTransitionState(target).requestId !== requestId) return;
    // 图→视频失败时继续留在图片层，避免白屏
    if (!wasVideoMedia) {
      video.style.opacity = "0";
      return;
    }
    target.removeAttribute("data-wallpaper-ready");
  };

  if (!isExactSameVideoUrl) {
    video.src = settings.url;
    video.load();
  } else if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    // 同源已缓冲时可立即展示
    revealVideoLayer(requestId);
  }

  void video.play().catch(() => undefined);
};

const revealWallpaperImage = (
  target: HTMLElement,
  nextImage: string,
  requestId: number
): void => {
  const state = getWallpaperTransitionState(target);
  if (state.requestId !== requestId) return;

  target.style.setProperty("--wallpaper-image", nextImage);
  target.style.removeProperty("--wallpaper-next-image");
  target.removeAttribute("data-wallpaper-transitioning");

  // 等浏览器完成背景绘制后再显示，避免透明根背景露出白底
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (state.requestId !== requestId) return;
      target.setAttribute("data-wallpaper-ready", "true");
      removeWallpaperVideoElement(target);
      state.pendingUrl = null;
      state.timers = [];
    });
  });
};

const startWallpaperRequest = (
  target: HTMLElement,
  pendingUrl: string
): number => {
  const state = getWallpaperTransitionState(target);
  clearWallpaperTimers(state);
  clearWallpaperPreload(state);
  target.removeAttribute("data-wallpaper-transitioning");
  target.removeAttribute("data-wallpaper-fade-out");
  target.style.removeProperty("--wallpaper-next-image");
  state.pendingUrl = pendingUrl;
  state.requestId += 1;
  return state.requestId;
};

const switchWallpaperWithCrossFade = (
  target: HTMLElement,
  nextImage: string,
  requestId: number
): void => {
  const state = getWallpaperTransitionState(target);
  clearWallpaperTimers(state);
  target.style.setProperty("--wallpaper-next-image", nextImage);
  target.setAttribute("data-wallpaper-transitioning", "true");

  const transitionTimer = window.setTimeout(() => {
    if (state.requestId !== requestId) return;
    revealWallpaperImage(target, nextImage, requestId);
  }, WALLPAPER_FADE_IN_MS);

  state.timers = [transitionTimer];
};

const preloadWallpaperImage = (
  target: HTMLElement,
  imageUrl: string,
  requestId: number,
  onReady: () => void,
  onError: () => void
): void => {
  const state = getWallpaperTransitionState(target);
  const img = new Image();
  state.preloadImage = img;

  const cleanup = () => {
    if (state.preloadImage !== img) return;
    img.onload = null;
    img.onerror = null;
    img.removeAttribute("src");
    state.preloadImage = null;
  };

  img.decoding = "async";
  img.onload = () => {
    if (state.requestId !== requestId) {
      cleanup();
      return;
    }
    cleanup();
    onReady();
  };
  img.onerror = () => {
    if (state.requestId === requestId) {
      onError();
    }
    cleanup();
  };
  img.src = imageUrl;
};

/**
 * 应用壁纸到 DOM
 * 设置 data-wallpaper 属性及壁纸 CSS 变量
 */
export const applyWallpaper = (
  settings: WallpaperSettings,
  element?: HTMLElement
): void => {
  const target = element || document.documentElement;

  if (settings.enabled && settings.url) {
    const mediaType = resolveWallpaperMediaType(settings);

    if (mediaType === "video") {
      applyWallpaperVideo(target, settings);
      return;
    }

    target.setAttribute("data-wallpaper", "true");
    target.setAttribute("data-wallpaper-media", "image");

    // 当前壁纸由 body::before 渲染，切换时新壁纸由 body::after 叠加淡入
    const nextImage = toWallpaperCssUrl(settings.url);
    const currentImage = target.style.getPropertyValue("--wallpaper-image");
    const hasCurrentImage = Boolean(currentImage && currentImage !== "none");
    const isWallpaperReady =
      target.getAttribute("data-wallpaper-ready") === "true";
    const state = getWallpaperTransitionState(target);

    // 仅当「完整 URL」一致时才复用进行中的预加载；
    // 同 path 不同签名（续签）必须取消旧请求，否则会卡在过期 URL 的失败预加载上
    const isPendingExactSameUrl =
      Boolean(state.pendingUrl) && state.pendingUrl === settings.url;

    // 已成功展示同一张图时，忽略签名 query 差异，避免无意义重绘闪白；
    // 若尚未 ready（预加载失败），即使 path 相同也要允许用新签名重试
    const isCurrentSameImageReady =
      isWallpaperReady &&
      hasCurrentImage &&
      isSameWallpaperUrl(currentImage, settings.url);

    if (isPendingExactSameUrl) {
      // 保持进行中的过渡，仅更新模糊/亮度
      applyWallpaperStyle(target, settings);
      return;
    }

    if (isCurrentSameImageReady) {
      // 同一张图已展示成功：只更新模糊/亮度，不重设 background-image
      applyWallpaperStyle(target, settings);
      return;
    }

    // 新图一律先预加载，加载完成后再显示/淡入，避免网络延迟白屏
    const requestId = startWallpaperRequest(target, settings.url);
    applyWallpaperStyle(target, settings);

    preloadWallpaperImage(
      target,
      settings.url,
      requestId,
      () => {
        if (hasCurrentImage && isWallpaperReady) {
          switchWallpaperWithCrossFade(target, nextImage, requestId);
          return;
        }
        revealWallpaperImage(target, nextImage, requestId);
      },
      () => {
        if (getWallpaperTransitionState(target).requestId !== requestId) {
          return;
        }
        clearWallpaperTransition(target);
        state.pendingUrl = null;
        // 加载失败时不标记 ready，保留上一张壁纸或默认背景，避免白屏
        if (hasCurrentImage && isWallpaperReady) {
          target.setAttribute("data-wallpaper-ready", "true");
        }
      }
    );

    target.style.removeProperty("--bg-primary");
    target.style.removeProperty("--bg-secondary");
    target.style.removeProperty("--bg-tertiary");
  } else {
    removeWallpaper(element);
  }
};

/**
 * 移除壁纸：清除 data-wallpaper 属性及所有内联 CSS 变量
 */
export const removeWallpaper = (element?: HTMLElement): void => {
  const target = element || document.documentElement;
  clearWallpaperTransition(target);
  getWallpaperTransitionState(target).requestId += 1;
  target.removeAttribute("data-wallpaper");
  target.removeAttribute("data-wallpaper-ready");
  target.removeAttribute("data-wallpaper-media");
  target.removeAttribute("data-wallpaper-transitioning");
  target.removeAttribute("data-wallpaper-fade-out");
  target.style.removeProperty("--wallpaper-image");
  target.style.removeProperty("--wallpaper-next-image");
  target.style.removeProperty("--wallpaper-blur");
  target.style.removeProperty("--wallpaper-brightness");
  target.style.removeProperty("--bg-primary");
  target.style.removeProperty("--bg-secondary");
  target.style.removeProperty("--bg-tertiary");
  removeWallpaperVideoElement(target);
};

// 导出类型和变量（可选，用于 TypeScript 类型检查）
export { lightTheme, darkTheme, type Theme } from "./variables";

export {
  CUSTOM_PRIMARY_STORAGE_KEY,
  DEFAULT_PRIMARY_BY_MODE,
  PRIMARY_OPACITY_STEPS,
  applyPrimaryColor,
  clearCustomPrimaryCssVars,
  getCustomPrimaryStorageKey,
  getDefaultPrimaryByMode,
  hexToRgba,
  isDefaultPrimaryColor,
  isSameHexColor,
  normalizeHex,
  readAppliedPrimaryColor,
  readStoredCustomPrimaryColors,
  resolveThemeColorTargets,
  restoreStoredCustomPrimaryColors,
  toOpaqueHex,
  writeStoredCustomPrimaryColor,
  type CustomPrimaryColors,
} from "./customPrimaryColor";

export {
  applyTextColor,
  clearCustomTextCssVars,
  getDefaultTextByMode,
  isDefaultTextColor,
  readAppliedTextColor,
  readStoredCustomTextColors,
  restoreStoredCustomTextColors,
  writeStoredCustomTextColor,
  type CustomTextColors,
} from "./customTextColor";

export {
  applyBtnPrimaryTextColor,
  clearCustomBtnPrimaryTextCssVars,
  getDefaultBtnPrimaryTextByMode,
  isDefaultBtnPrimaryTextColor,
  readAppliedBtnPrimaryTextColor,
  readStoredCustomBtnPrimaryTextColors,
  restoreStoredCustomBtnPrimaryTextColors,
  writeStoredCustomBtnPrimaryTextColor,
  type CustomBtnPrimaryTextColors,
} from "./customBtnPrimaryTextColor";

export {
  applyBtnDeputyTextColor,
  clearCustomBtnDeputyTextCssVars,
  getDefaultBtnDeputyTextByMode,
  isDefaultBtnDeputyTextColor,
  readAppliedBtnDeputyTextColor,
  readStoredCustomBtnDeputyTextColors,
  restoreStoredCustomBtnDeputyTextColors,
  writeStoredCustomBtnDeputyTextColor,
  type CustomBtnDeputyTextColors,
} from "./customBtnDeputyTextColor";
